import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Search, Filter, Edit2, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { appointmentsApi } from '../services/api';
import AppointmentModal from '../components/AppointmentModal';
import { useAuth } from '../contexts/AuthContext';
import { Appointment } from '../types';

type AppointmentSortField = 'patient' | 'title' | 'startTime' | 'dentist' | 'status' | 'type';
type SortDirection = 'asc' | 'desc';

const Appointments: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [sortField, setSortField] = useState<AppointmentSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const queryClient = useQueryClient();

  // Check if user is a patient
  const isPatient = user?.role === 'patient';

  // Fetch appointments with patient filter if user is a patient
  const { data: appointmentsResponse, isLoading } = useQuery({
    queryKey: ['appointments', searchTerm, statusFilter, currentPage, isPatient ? user?._id : null],
    queryFn: () => appointmentsApi.getAll({
      search: searchTerm,
      status: statusFilter,
      page: currentPage,
      patient: isPatient ? user?._id : undefined
    }),
  });

  const appointments = Array.isArray(appointmentsResponse?.data?.appointments) 
    ? appointmentsResponse!.data!.appointments 
    : Array.isArray(appointmentsResponse?.data) 
    ? appointmentsResponse!.data 
    : [];

  const createAppointmentMutation = useMutation({
    mutationFn: appointmentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setIsModalOpen(false);
      setSelectedAppointment(null);
    },
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => appointmentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setIsModalOpen(false);
      setSelectedAppointment(null);
    },
  });

  const deleteAppointmentMutation = useMutation({
    mutationFn: appointmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setIsModalOpen(false);
      setSelectedAppointment(null);
    },
  });

  const handleCreateAppointment = () => {
    setSelectedAppointment(null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    // Transformar la cita al formato esperado por el modal
    const appointmentForModal = {
      ...appointment,
      start: appointment.startTime,
      end: appointment.endTime,
      title: appointment.title,
      description: appointment.notes || '',
      status: appointment.status,
      type: appointment.type,
      dentist: appointment.dentist || '',
      room: appointment.room ?? '',
      notes: appointment.notes || '',
      patient: typeof appointment.patient === 'object' ? {
        _id: appointment.patient._id,
        firstName: appointment.patient.firstName || '',
        lastName: appointment.patient.lastName || ''
      } : {
        _id: appointment.patient || '',
        firstName: '',
        lastName: ''
      }
    };
    
    setSelectedAppointment(appointmentForModal as any);
    setIsModalOpen(true);
  };

  const handleDeleteAppointment = async (appointment: Appointment) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta cita?')) {
      deleteAppointmentMutation.mutate(appointment._id);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleSaveAppointment = (data: any) => {
    if (selectedAppointment) {
      // Check if status changed to cancelled - use specific status endpoint for notifications
      const statusChanged = selectedAppointment.status !== data.status;
      const isCancellation = data.status === 'cancelled';
      
      if (statusChanged && isCancellation) {
        console.log('🚫 Cancelling appointment - using status endpoint for notifications');
        // First update the status using the notification-enabled endpoint
        appointmentsApi.updateStatus(selectedAppointment._id, 'cancelled')
          .then(() => {
            console.log('✅ Status updated to cancelled');
            // Then update other fields if needed
            const { status, patient, ...otherData } = data;
            const transformedData: any = { ...otherData };
            if (patient) transformedData.patientId = patient;
            
            if (Object.keys(transformedData).length > 0) {
              return appointmentsApi.update(selectedAppointment._id, transformedData);
            }
          })
          .then(() => {
            console.log('✅ Appointment fully updated');
            queryClient.invalidateQueries({ queryKey: ['appointments'] });
            setIsModalOpen(false);
            setSelectedAppointment(null);
          })
          .catch((error) => {
            console.error('❌ Error updating appointment:', error);
          });
      } else {
        // Normal update - transform data for backend
        const updateData: any = {};
        if (data.patient) updateData.patientId = data.patient;
        if (data.title) updateData.title = data.title;
        if (data.description !== undefined) updateData.description = data.description;
        if (data.startTime) updateData.startTime = data.startTime;
        if (data.endTime) updateData.endTime = data.endTime;
        if (data.status) updateData.status = data.status;
        if (data.type) updateData.type = data.type;
        if (data.dentist) updateData.dentist = data.dentist;
        if (data.room !== undefined) updateData.room = data.room;
        if (data.notes !== undefined) updateData.notes = data.notes;
        
        updateAppointmentMutation.mutate({
          id: selectedAppointment._id,
          data: updateData,
        });
      }
    } else {
      // Transform data for backend
      const appointmentData: any = {
        patientId: data.patient, // Transform 'patient' to 'patientId'
        title: data.title,
        description: data.description,
        startTime: data.startTime,
        endTime: data.endTime,
        status: data.status,
        type: data.type,
        dentist: data.dentist,
        room: data.room,
        notes: data.notes,
      };
      
      createAppointmentMutation.mutate(appointmentData);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      scheduled: 'Programada',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      'no-show': 'No se presentó',
    };
    return labels[status as keyof typeof labels] || status;
  };

  const handleSort = (field: AppointmentSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: AppointmentSortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-dental-600" />
      : <ArrowDown className="h-4 w-4 text-dental-600" />;
  };

  const sortedAppointments = useMemo(() => {
    // Ensure appointments is always an array
    const appointmentsArray = Array.isArray(appointments) ? appointments : [];
    
    if (!sortField) return appointmentsArray;

    return [...appointmentsArray].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'patient':
          aValue = typeof a.patient === 'object' 
            ? `${a.patient?.firstName} ${a.patient?.lastName}`.toLowerCase()
            : '';
          bValue = typeof b.patient === 'object' 
            ? `${b.patient?.firstName} ${b.patient?.lastName}`.toLowerCase()
            : '';
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'startTime':
          aValue = new Date(a.startTime).getTime();
          bValue = new Date(b.startTime).getTime();
          break;
        case 'dentist':
          aValue = a.dentist.toLowerCase();
          bValue = b.dentist.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [appointments, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isPatient ? 'Mis Citas' : 'Citas'}
          </h1>
          <p className="text-gray-600">
            {isPatient 
              ? 'Consulta tus citas programadas y solicita nuevas citas' 
              : 'Gestiona todas las citas de la clínica'
            }
          </p>
        </div>
        <button
          onClick={handleCreateAppointment}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dental-600 hover:bg-dental-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          {isPatient ? 'Solicitar Cita' : 'Nueva Cita'}
        </button>
      </div>

      {/* Search and filters for staff */}
      {!isPatient && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar citas por paciente, dentista o título..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
              >
                <option value="">Todos los estados</option>
                <option value="scheduled">Programada</option>
                <option value="confirmed">Confirmada</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
                <option value="no-show">No se presentó</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Simple filter for patients */}
      {isPatient && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
              >
                <option value="">Todas mis citas</option>
                <option value="scheduled">Programadas</option>
                <option value="confirmed">Confirmadas</option>
                <option value="completed">Completadas</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Appointments table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {!isPatient && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('patient')}
                      >
                        <div className="flex items-center justify-between">
                          Paciente
                          {getSortIcon('patient')}
                        </div>
                      </th>
                    )}
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center justify-between">
                        Cita
                        {getSortIcon('title')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('startTime')}
                    >
                      <div className="flex items-center justify-between">
                        Fecha y Hora
                        {getSortIcon('startTime')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('dentist')}
                    >
                      <div className="flex items-center justify-between">
                        Dentista
                        {getSortIcon('dentist')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-between">
                        Estado
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedAppointments.map((appointment: any) => (
                    <tr key={appointment._id} className="hover:bg-gray-50">
                      {!isPatient && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-dental-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-dental-600">
                                  {appointment.patient?.firstName?.[0] || 'P'}{appointment.patient?.lastName?.[0] || 'P'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {appointment.patient?.firstName} {appointment.patient?.lastName}
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{appointment.title}</div>
                        <div className="text-sm text-gray-500">{appointment.type}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(appointment.startTime)}</div>
                        <div className="text-sm text-gray-500">
                          {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.dentist}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          appointment.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'completed'
                            ? 'bg-blue-100 text-blue-800'
                            : appointment.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : appointment.status === 'no-show'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {getStatusLabel(appointment.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Patients can only edit their own appointments if not completed/cancelled */}
                          {(isPatient && ['scheduled', 'confirmed'].includes(appointment.status)) || !isPatient ? (
                            <button
                              onClick={() => handleEditAppointment(appointment)}
                              className="text-dental-600 hover:text-dental-900"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          ) : null}
                          
                          {/* Only staff can delete appointments */}
                          {!isPatient && (
                            <button
                              onClick={() => handleDeleteAppointment(appointment)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {appointments.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {isPatient ? 'No tienes citas programadas.' : 'No se encontraron citas.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Appointment Modal */}
      {isModalOpen && (
        <AppointmentModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveAppointment}
          onDelete={selectedAppointment ? () => handleDeleteAppointment(selectedAppointment) : undefined}
          appointment={selectedAppointment as unknown as any}
          isLoading={
            updateAppointmentMutation.isLoading ||
            deleteAppointmentMutation.isLoading
          }
        />
      )}
    </div>
  );
};

export default Appointments;